import { createContext, type ReactNode, useContext, useMemo } from 'react';

import usePinnedItems, { type UsePinnedItemsReturn } from '@/shared/hooks/usePinnedItems';

import {
  FINANCE_TAB_KEYS,
  FINANCE_TABS_STORAGE_KEY,
} from '@/containers/HomeownerAssociation/Finance/financeTabs.constants';
import { SUB_NAV_SECTIONS, type SubNavSectionKey } from '../subNavSections.constants';

export type ManageableSectionKey = 'accounting' | SubNavSectionKey;

// eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
type PinnedSubNavMap = Record<ManageableSectionKey, UsePinnedItemsReturn>;

const PinnedSubNavContext = createContext<PinnedSubNavMap | null>(null);

const sectionAllKeys = (key: SubNavSectionKey) =>
  SUB_NAV_SECTIONS[key].items.map((item) => item.key);

export const PinnedSubNavProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const accounting = usePinnedItems({
    allKeys: FINANCE_TAB_KEYS,
    defaultOrder: FINANCE_TAB_KEYS,
    storageKey: FINANCE_TABS_STORAGE_KEY,
  });
  const budget = usePinnedItems({
    allKeys: sectionAllKeys('budget'),
    defaultOrder: sectionAllKeys('budget'),
    storageKey: SUB_NAV_SECTIONS.budget.storageKey,
  });
  const buildings = usePinnedItems({
    allKeys: sectionAllKeys('buildings'),
    defaultOrder: sectionAllKeys('buildings'),
    storageKey: SUB_NAV_SECTIONS.buildings.storageKey,
  });
  const debtors = usePinnedItems({
    allKeys: sectionAllKeys('debtors'),
    defaultOrder: sectionAllKeys('debtors'),
    storageKey: SUB_NAV_SECTIONS.debtors.storageKey,
  });
  const directDebit = usePinnedItems({
    allKeys: sectionAllKeys('directDebit'),
    defaultOrder: sectionAllKeys('directDebit'),
    storageKey: SUB_NAV_SECTIONS.directDebit.storageKey,
  });
  const documents = usePinnedItems({
    allKeys: sectionAllKeys('documents'),
    defaultOrder: sectionAllKeys('documents'),
    storageKey: SUB_NAV_SECTIONS.documents.storageKey,
  });
  const meetings = usePinnedItems({
    allKeys: sectionAllKeys('meetings'),
    defaultOrder: sectionAllKeys('meetings'),
    storageKey: SUB_NAV_SECTIONS.meetings.storageKey,
  });
  const meterReadings = usePinnedItems({
    allKeys: sectionAllKeys('meterReadings'),
    defaultOrder: sectionAllKeys('meterReadings'),
    storageKey: SUB_NAV_SECTIONS.meterReadings.storageKey,
  });
  const payments = usePinnedItems({
    allKeys: sectionAllKeys('payments'),
    defaultOrder: sectionAllKeys('payments'),
    storageKey: SUB_NAV_SECTIONS.payments.storageKey,
  });
  const provisions = usePinnedItems({
    allKeys: sectionAllKeys('provisions'),
    defaultOrder: sectionAllKeys('provisions'),
    storageKey: SUB_NAV_SECTIONS.provisions.storageKey,
  });
  const settings = usePinnedItems({
    allKeys: sectionAllKeys('settings'),
    defaultOrder: sectionAllKeys('settings'),
    storageKey: SUB_NAV_SECTIONS.settings.storageKey,
  });
  const syndicManagement = usePinnedItems({
    allKeys: sectionAllKeys('syndicManagement'),
    defaultOrder: sectionAllKeys('syndicManagement'),
    storageKey: SUB_NAV_SECTIONS.syndicManagement.storageKey,
  });

  const value = useMemo<PinnedSubNavMap>(
    () => ({
      accounting,
      budget,
      buildings,
      debtors,
      directDebit,
      documents,
      meetings,
      meterReadings,
      payments,
      provisions,
      settings,
      syndicManagement,
    }),
    [
      accounting,
      budget,
      buildings,
      debtors,
      directDebit,
      documents,
      meetings,
      meterReadings,
      payments,
      provisions,
      settings,
      syndicManagement,
    ],
  );

  return <PinnedSubNavContext.Provider value={value}>{children}</PinnedSubNavContext.Provider>;
};

export const usePinnedSubNavSection = (key: ManageableSectionKey): UsePinnedItemsReturn => {
  const map = useContext(PinnedSubNavContext);

  if (!map) {
    throw new Error('usePinnedSubNavSection must be used within a PinnedSubNavProvider');
  }

  return map[key];
};

// Same lookup, but for components (e.g. EnvelopeOverview) that are also mounted outside the
// homeowner-association routes, where there is no PinnedSubNavProvider and nothing to pin.
export const useOptionalPinnedSubNavSection = (
  key: ManageableSectionKey,
): UsePinnedItemsReturn | null => {
  const map = useContext(PinnedSubNavContext);

  return map ? map[key] : null;
};

export default PinnedSubNavContext;
